import React from 'react';
import { cn } from '@/utils/cn';
import {
  dashboardTableCellClass,
  dashboardTableDataRowClass,
  dashboardTableHeadClass,
  dashboardTableHeaderRowClass,
} from '@/components/dashboard/DashboardSectionCard';

/** Strip table-cell width utilities that break mobile card layout. */
function mobileDetailDdClassName(className?: string): string {
  if (!className) {
    return 'min-w-0 flex-1 text-gray-700 dark:text-gray-300';
  }
  const stripped = className
    .replace(/\bw-\[[^\]]+\]/g, '')
    .replace(/\bw-\d+%?\b/g, '')
    .replace(/\bwhitespace-nowrap\b/g, '')
    .trim();
  return cn('min-w-0 flex-1 text-gray-700 dark:text-gray-300', stripped);
}

export interface PaginatedTableColumn<T> {
  key: string;
  header: React.ReactNode;
  render: (item: T) => React.ReactNode;
  /** Optional class for the th/td (e.g. text-right) */
  className?: string;
}

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedTableProps<T> {
  columns: PaginatedTableColumn<T>[];
  data: T[];
  pagination: PaginationState | null;
  loading?: boolean;
  emptyMessage?: string;
  onPageChange: (page: number) => void;
  /** Optional key extractor; defaults to (item, index) => (item as { id?: string }).id ?? index */
  getRowKey?: (item: T, index: number) => string | number;
  /** Wraps the table (e.g. card surface) */
  className?: string;
  /** When set, entire row is clickable (use stopPropagation on interactive cells). */
  onRowClick?: (item: T) => void;
  /** Omit default p-4 wrapper padding (e.g. nested in a card body). */
  noWrapperPadding?: boolean;
  /**
   * Column key to use as the mobile card title. Defaults to the first column's key.
   * Set to a non-existent key to show no title row (all fields as label/value only).
   */
  mobileTitleColumnKey?: string;
  /** When set, replaces the default label/value mobile card layout per row. */
  renderMobileCard?: (item: T, index: number) => React.ReactNode;
  /** When `dashboard`, matches Coach hours / billing report table styling. */
  variant?: 'dashboard' | 'legacy';
}

/**
 * Reusable paginated table: header, body, optional loading/empty state, and prev/next pagination.
 * On viewports below `md`, rows render as stacked cards for readability.
 */
export function PaginatedTable<T>({
  columns,
  data,
  pagination,
  loading = false,
  emptyMessage = 'No items found.',
  onPageChange,
  getRowKey = (item, index) => (item as { id?: string }).id ?? index,
  className,
  onRowClick,
  noWrapperPadding = false,
  mobileTitleColumnKey,
  renderMobileCard,
  variant = 'dashboard',
}: PaginatedTableProps<T>) {
  const wrapperClass = ['overflow-x-auto', !noWrapperPadding ? 'p-4' : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  const titleKey =
    mobileTitleColumnKey === ''
      ? null
      : mobileTitleColumnKey ?? columns[0]?.key ?? null;

  const titleColumn = titleKey ? columns.find((c) => c.key === titleKey) : undefined;
  const detailColumns = titleColumn ? columns.filter((c) => c.key !== titleKey) : columns;
  const actionsColumn = detailColumns.find((c) => c.key === 'actions');
  const bodyColumns = detailColumns.filter((c) => c.key !== 'actions');

  return (
    <div className={wrapperClass}>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400" />
        </div>
      ) : (
        <>
          {/* Mobile: card stack */}
          <div className={cn('md:hidden', renderMobileCard ? 'space-y-2' : 'space-y-3')}>
            {data.map((item, index) =>
              renderMobileCard ? (
                <React.Fragment key={getRowKey(item, index)}>{renderMobileCard(item, index)}</React.Fragment>
              ) : (
                <div
                  key={getRowKey(item, index)}
                  role={onRowClick ? 'button' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(item) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(item);
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    'rounded-lg border border-border bg-card p-3 text-sm shadow-sm sm:p-4',
                    onRowClick &&
                      'cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                >
                  {titleColumn && (
                    <div className="mb-2 border-b border-border pb-2 text-sm font-semibold text-foreground sm:mb-3 sm:text-base">
                      {titleColumn.render(item)}
                    </div>
                  )}
                  <dl className="space-y-1.5">
                    {bodyColumns.map((col) => (
                      <div
                        key={col.key}
                        className="flex items-start justify-between gap-2 border-b border-border/60 pb-1.5 last:border-0 last:pb-0 sm:gap-3 sm:pb-2"
                      >
                        <dt className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:text-xs">
                          {col.header}
                        </dt>
                        <dd className={mobileDetailDdClassName(col.className)}>
                          {col.render(item)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  {actionsColumn ? (
                    <div
                      className="mt-2 flex justify-end gap-2 border-t border-border/60 pt-2 sm:mt-3 sm:pt-3"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {actionsColumn.render(item)}
                    </div>
                  ) : null}
                </div>
              )
            )}
          </div>

          {/* Desktop: table */}
          <table className="hidden w-full text-sm md:table">
            <thead>
              <tr
                className={
                  variant === 'dashboard'
                    ? dashboardTableHeaderRowClass
                    : 'border-b border-border bg-muted/30 dark:bg-muted/20'
                }
              >
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      variant === 'dashboard'
                        ? dashboardTableHeadClass
                        : 'py-2 text-left font-medium text-gray-900 dark:text-white',
                      col.className
                    )}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr
                  key={getRowKey(item, index)}
                  role={onRowClick ? 'button' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(item) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(item);
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    variant === 'dashboard'
                      ? dashboardTableDataRowClass
                      : 'border-b border-border/80 odd:bg-background even:bg-muted/5 dark:even:bg-muted/10',
                    onRowClick &&
                      'cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        variant === 'dashboard'
                          ? cn(dashboardTableCellClass, 'text-foreground')
                          : 'py-2 text-gray-700 dark:text-gray-300',
                        col.className
                      )}
                    >
                      {col.render(item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {data.length === 0 && (
            <p className="py-8 text-center text-gray-500 dark:text-gray-400">{emptyMessage}</p>
          )}
          {pagination && pagination.totalPages > 1 && (
            <div
              className={cn(
                'mt-4 flex flex-col gap-3 border-t border-border pt-4 pb-4 sm:flex-row sm:items-center sm:justify-between',
                noWrapperPadding && 'px-4'
              )}
            >
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
                  disabled={pagination.page <= 1}
                  className="rounded border border-input px-3 py-1 text-sm hover:bg-muted/50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="rounded border border-input px-3 py-1 text-sm hover:bg-muted/50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
